import { alertApiRef, useApi } from '@mainstage/core-plugin-api';
import { wittyAssistantApiRef, WittySessionSummary } from '../api';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { WittyMessage } from '@agilelab/plugin-wb-platform';
import { handleWittyError } from '../hooks';

export type WittyAssistantHook = {
  messages: WittyMessage[];
  open: boolean;
  loading: boolean;
  statusText: string;
  onNewMessage: (message: WittyMessage) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  expanded: boolean;
  setExpanded: Function;
  onNewSession: Function;
  historyOpen: boolean;
  setHistoryOpen: Dispatch<SetStateAction<boolean>>;
  sessions: WittySessionSummary[];
  sessionsLoading: boolean;
  loadSessions: () => void;
  loadMoreSessions: () => void;
  hasMoreSessions: boolean;
  searchSessions: (query: string) => void;
  sessionSearchQuery: string;
  onSelectSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
  activeSessionId: string | null;
};

export function useWittyAssistant(): WittyAssistantHook {
  const wittyAssistantApi = useApi(wittyAssistantApiRef);
  const alertApi = useApi(alertApiRef);

  const [messages, setMessages] = useState<WittyMessage[]>([]);
  const [loading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const [open, setOpen] = useState(false);

  // History sidebar state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState<WittySessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [sessionSearchQuery, setSessionSearchQuery] = useState('');
  const searchQueryRef = useRef('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PAGE_SIZE = 10;

  const loadSessions = useCallback(() => {
    setSessionsLoading(true);
    setSessionsOffset(0);
    const q = searchQueryRef.current || undefined;
    wittyAssistantApi
      .getSessions(PAGE_SIZE, 0, q)
      .then(result => {
        setSessions(result);
        setHasMoreSessions(result.length >= PAGE_SIZE);
        setSessionsOffset(PAGE_SIZE);
      })
      .catch(() => {
        setSessions([]);
        setHasMoreSessions(false);
      })
      .finally(() => setSessionsLoading(false));
  }, [wittyAssistantApi]);

  const loadMoreSessions = useCallback(() => {
    if (sessionsLoading || !hasMoreSessions) return;
    setSessionsLoading(true);
    const q = searchQueryRef.current || undefined;
    wittyAssistantApi
      .getSessions(PAGE_SIZE, sessionsOffset, q)
      .then(result => {
        setSessions(prev => [...prev, ...result]);
        setHasMoreSessions(result.length >= PAGE_SIZE);
        setSessionsOffset(prev => prev + PAGE_SIZE);
      })
      .catch(() => setHasMoreSessions(false))
      .finally(() => setSessionsLoading(false));
  }, [wittyAssistantApi, sessionsOffset, sessionsLoading, hasMoreSessions]);

  const searchSessions = useCallback(
    (query: string) => {
      searchQueryRef.current = query;
      setSessionSearchQuery(query);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSessionsLoading(true);
        setSessionsOffset(0);
        wittyAssistantApi
          .getSessions(PAGE_SIZE, 0, query || undefined)
          .then(result => {
            setSessions(result);
            setHasMoreSessions(result.length >= PAGE_SIZE);
            setSessionsOffset(PAGE_SIZE);
          })
          .catch(() => {
            setSessions([]);
            setHasMoreSessions(false);
          })
          .finally(() => setSessionsLoading(false));
      }, 300);
    },
    [wittyAssistantApi],
  );

  const onSelectSession = useCallback(
    (selectedSessionId: string) => {
      setSessionId(selectedSessionId);
      setIsLoading(true);
      wittyAssistantApi
        .getHistory(selectedSessionId)
        .then(history => {
          const restored: WittyMessage[] = history.map(m => ({
            sender: m.type === 'human' ? 'you' : 'witty',
            contentMessage: m.content,
          }));
          setMessages(restored);
        })
        .catch(error => {
          handleWittyError(error, alertApi);
        })
        .finally(() => setIsLoading(false));
    },
    [wittyAssistantApi, alertApi],
  );

  const onNewMessage = useCallback(
    (message: WittyMessage) => {
      setMessages(oldMessages => [...oldMessages, message]);
      setIsLoading(true);
      setStatusText('');

      let streamedReplyStarted = false;

      wittyAssistantApi
        .chat(
          {
            request: {
              message: message.contentMessage,
              ...(sessionId ? { session_id: sessionId } : {}),
            },
          },
          token => {
            setIsLoading(false);
            setStatusText('');
            if (!streamedReplyStarted) {
              streamedReplyStarted = true;
              setMessages(oldMessages => [
                ...oldMessages,
                { sender: 'witty', contentMessage: token },
              ]);
            } else {
              setMessages(oldMessages => {
                const updatedMessages = [...oldMessages];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage?.sender === 'witty') {
                  updatedMessages[updatedMessages.length - 1] = {
                    ...lastMessage,
                    contentMessage: `${lastMessage.contentMessage}${token}`,
                  };
                }
                return updatedMessages;
              });
            }
          },
          status => {
            flushSync(() => {
              setStatusText(status);
            });
          },
        )
        .then(response => {
          if (!streamedReplyStarted && response.message) {
            const newMessage: WittyMessage = {
              sender: 'witty',
              contentMessage: response.message,
            };
            setMessages(oldMessages => [...oldMessages, newMessage]);
          }

          if (response.session_id) {
            setSessionId(response.session_id);
          }
        })
        .catch((error: any) => {
          handleWittyError(error, alertApi);
        })
        .finally(() => {
          setIsLoading(false);
          setStatusText('');
        });
    },
    [wittyAssistantApi, alertApi, sessionId],
  );

  const onNewSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
  }, []);

  const onRenameSession = useCallback(
    async (targetSessionId: string, newTitle: string) => {
      await wittyAssistantApi.renameSession(targetSessionId, newTitle);
      setSessions(prev =>
        prev.map(s =>
          s.session_id === targetSessionId ? { ...s, title: newTitle } : s,
        ),
      );
    },
    [wittyAssistantApi],
  );

  const onDeleteSession = useCallback(
    async (targetSessionId: string) => {
      await wittyAssistantApi.deleteSession(targetSessionId);
      setSessions(prev => prev.filter(s => s.session_id !== targetSessionId));
      if (sessionId === targetSessionId) {
        setSessionId(null);
        setMessages([]);
      }
    },
    [wittyAssistantApi, sessionId],
  );

  return {
    loading,
    statusText,
    messages,
    open,
    onNewMessage,
    setOpen,
    expanded,
    setExpanded,
    onNewSession,
    historyOpen,
    setHistoryOpen,
    sessions,
    sessionsLoading,
    loadSessions,
    loadMoreSessions,
    hasMoreSessions,
    searchSessions,
    sessionSearchQuery,
    onSelectSession,
    onRenameSession,
    onDeleteSession,
    activeSessionId: sessionId,
  };
}
